import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';

/**
 * Breadcrumb with Schema.org BreadcrumbList JSON-LD.
 *
 * Usage:
 *   <Breadcrumb items={[
 *     { label: 'Home', to: '/' },
 *     { label: 'For Doctors', to: '/for/cardiology' },
 *     { label: 'Cardiology' }   // last item is current page, no `to`
 *   ]} />
 */
export default function Breadcrumb({ items = [] }) {
  if (!items.length) return null;

  const origin = typeof window !== 'undefined' ? window.location.origin : 'https://docsite.care';
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((it, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: it.label,
      ...(it.to ? { item: `${origin}${it.to}` } : {}),
    })),
  };

  return (
    <>
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>
      <nav aria-label="Breadcrumb" className="text-xs text-slate-500" data-testid="breadcrumb">
        <ol className="flex flex-wrap items-center gap-1">
          {items.map((it, i) => (
            <li key={i} className="flex items-center gap-1">
              {i > 0 && <ChevronRight size={12} className="text-slate-300" />}
              {it.to ? (
                <Link to={it.to} className="hover:text-sky-600 transition-colors">{it.label}</Link>
              ) : (
                <span className="text-slate-700 font-medium">{it.label}</span>
              )}
            </li>
          ))}
        </ol>
      </nav>
    </>
  );
}
